import { Table, Button, Grid, Message, Empty, Alert, Radio, Select, Checkbox, Image, Space, Typography, Dropdown, Menu, Modal, Input } from "@arco-design/web-react";
import { IconDelete, IconRefresh, IconApps, IconList, IconFile, IconTag } from "@arco-design/web-react/icon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Content from "../Layout/Content";

import { useAuth0 } from "@auth0/auth0-react";
import { DeleteApi, FetchApi, PostApi, UploadFileApi } from "../Api/AuthenticatedApi";

import "react-csv-importer/dist/index.css";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import SummaryCard from "../Layout/SummaryCard";
import DeleteFilesDialog from "./DeleteFilesDialog";
import StyledDropZone from "./StyledDropZone";
import FileCard from "./FileCard";
import FileVersionsModal from "./FileVersionsModal";
import FileUsageLinks from "./FileUsageLinks";
import { groupUsages } from "./fileUsage";
import { confirmUpload, uploadFields } from "./shrinkNotice";
import FileTagPicker from "./FileTagPicker";
import {
    DEFAULT_SORT,
    SORT_OPTIONS,
    UNTAGGED,
    filterByTag,
    formatBytes,
    formatDimensions,
    imageCount,
    isImageFile,
    previewUrl,
    thumbUrl,
    fileDimensions,
    sortFiles,
    tagUsage,
} from "./fileMeta";

const { Row, Col } = Grid;
const { Text } = Typography;

// Files are mostly images — logos, banners, the art that goes into an email —
// and a list of names tells you nothing about which one you want. The listing
// API signs a short-lived preview URL per image, so both views show the picture
// and clicking it opens the full size.
const VIEW_STORAGE_KEY = "fileAssets.view";

function storedView() {
    try {
        const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
        return saved === "list" || saved === "grid" ? saved : "grid";
    } catch (e) {
        // Private browsing / blocked storage: the default view is fine.
        return "grid";
    }
}

export default function FileAssets() {
    let history = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [fileAssetItems, setFileAssetItems] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const [selected, setSelected] = useState([]);
    const [view, setView] = useState(storedView);
    const [sort, setSort] = useState(DEFAULT_SORT);
    // Natural sizes, keyed by file name, filled in as thumbnails load — the
    // listing API doesn't report image dimensions.
    const [dimensions, setDimensions] = useState({});
    // File tags are their own vocabulary, separate from contact tags: these
    // name artwork (campaign, brand, purpose), those name audiences.
    const [fileTags, setFileTags] = useState([]);
    const [tagFilterId, setTagFilterId] = useState("");
    const [newTagName, setNewTagName] = useState("");
    // When set, the tag being created will be applied to these files once it
    // exists — so "＋ New tag…" from a file both creates and applies it.
    const [pendingTagTargets, setPendingTagTargets] = useState(null);

    const refreshRemoteFileList = useCallback(() => {
        FetchApi(getAccessTokenSilently,
            "/api/files/",
            (data) => {
                setFileAssetItems(data);
                setSelected([]);
                // A re-uploaded file keeps its name but may be a different
                // picture, so measurements start over with the new listing.
                setDimensions({});
                setLoadError(false);
            },
            (response) => {
                history("/login");
            }, (response) => {
                // Non-auth failure (e.g. storage/S3 not configured on the server).
                // Surface a persistent, actionable message instead of a toast that
                // leaves the table reading as an empty "No files yet".
                setLoadError(true);
            })
    }, [getAccessTokenSilently, history, setFileAssetItems, setSelected])

    const refreshTags = useCallback(() => {
        FetchApi(getAccessTokenSilently,
            "/api/file-tags/",
            (data) => setFileTags(Array.isArray(data) ? data : []),
            () => history("/login"),
            () => {
                // Tags are an extra on this page; failing to load them mustn't
                // take the file list down with it.
                setFileTags([]);
            })
    }, [getAccessTokenSilently, history]);

    // Applying or clearing a tag re-fetches rather than patching state locally:
    // the listing is one request and the chips must match what's stored.
    const toggleTag = useCallback((files, tagId, add) => {
        const names = (Array.isArray(files) ? files : [files]).map((f) => (typeof f === "string" ? f : f.name));
        PostApi(getAccessTokenSilently,
            `/api/file-tags/${tagId}/${add ? "assign" : "remove"}`,
            () => refreshRemoteFileList(),
            () => history("/login"),
            () => Message.error(add ? "Failed to apply tag" : "Failed to remove tag"),
            { file_names: names })
    }, [getAccessTokenSilently, history, refreshRemoteFileList]);

    const openNewTagFor = useCallback((files) => {
        const list = files == null ? [] : Array.isArray(files) ? files : [files];
        setPendingTagTargets(list);
        setNewTagName("");
    }, []);

    const createTag = useCallback(() => {
        const name = newTagName.trim();
        if (!name) return;
        const targets = pendingTagTargets || [];
        PostApi(getAccessTokenSilently,
            "/api/file-tags/",
            (created) => {
                setPendingTagTargets(null);
                setNewTagName("");
                refreshTags();
                if (created && created.id && targets.length > 0) {
                    toggleTag(targets, created.id, true);
                }
            },
            () => history("/login"),
            () => Message.error("Failed to create tag"),
            { name, color: "" })
    }, [getAccessTokenSilently, history, newTagName, pendingTagTargets, refreshTags, toggleTag]);

    const changeView = useCallback((next) => {
        setView(next);
        try {
            window.localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch (e) {
            // Not worth failing the click over.
        }
    }, []);

    const recordDimensions = useCallback((name, dims) => {
        setDimensions((prev) => (prev[name] ? prev : { ...prev, [name]: dims }));
    }, []);

    const toggleSelect = useCallback((name) => {
        setSelected((prev) => (prev.indexOf(name) > -1 ? prev.filter((n) => n !== name) : prev.concat(name)));
    }, []);

    // loadUsages resolves the forms and emails that show any of the files.
    const loadUsages = useCallback((names) => new Promise((resolve, reject) => {
        PostApi(getAccessTokenSilently, "/api/files/usages", resolve, () => history("/login"), reject, { names });
    }), [getAccessTokenSilently, history]);

    // Where each file is used (forms, emails, giveaways), by file name. One
    // request for the whole list; null until it's back, {} if it fails.
    const [usagesByName, setUsagesByName] = useState(null);
    useEffect(() => {
        const names = Array.isArray(fileAssetItems) ? fileAssetItems.map((f) => f.name) : [];
        if (names.length === 0) {
            setUsagesByName({});
            return;
        }
        let live = true;
        setUsagesByName(null);
        loadUsages(names).then(
            (list) => {
                if (!live) return;
                const byName = {};
                groupUsages(list).forEach((g) => { byName[g.fileName] = g.usages; });
                setUsagesByName(byName);
            },
            () => live && setUsagesByName({})
        );
        return () => { live = false; };
    }, [fileAssetItems, loadUsages]);
    const usagesFor = (name) => (usagesByName ? usagesByName[name] || [] : undefined);

    function MyDeleteFilesDialog({ totalCount }) {
        return (
            <>
                <DeleteFilesDialog
                    count={selected.length}
                    names={selected}
                    loadUsages={loadUsages}
                    onSave={() => {
                            DeleteApi(getAccessTokenSilently,
                                "/api/files/",
                                (data) => {
                                    setSelected([]);
                                    refreshRemoteFileList();
                                    Message.success("File deleted");
                                },
                                (response) => {
                                    // setInitializing(false);
                                    history("/login");
                                }, (response) => {
                                    // setInitializing(false);
                                    Message.error("Failed to delete file");
                                },
                                selected.map((file, index) => {
                                    return {'id': file, 'name': file}
                                }),
                            )
                    }}
                    render={(open) => (
                        <Button
                            type="outline"
                            icon={<IconDelete />}
                            onClick={open}
                            className="dialog-button"
                        >
                            <>Delete {selected.length}</>
                        </Button>
                    )}
                />
            </>
        )
    }

    useEffect(() => {
        refreshRemoteFileList();
        refreshTags();
    }, [history, refreshRemoteFileList, refreshTags])

    // downloadFull opens the full-size original kept of a shrunk image.
    const downloadFull = useCallback((name) => {
        FetchApi(getAccessTokenSilently,
            "/api/files/" + encodeURIComponent(name) + "?full=1",
            (data) => window.open(data.url, "_blank", "noopener"),
            () => history("/login"),
            () => Message.error("Failed to get the full-size image"));
    }, [getAccessTokenSilently, history]);

    // The image whose stored copies are being shown, and those copies (null
    // while they load).
    const [versionsFile, setVersionsFile] = useState(null);
    const [versions, setVersions] = useState(null);
    const showVersions = useCallback((file) => {
        setVersionsFile(file);
        setVersions(null);
        FetchApi(getAccessTokenSilently,
            "/api/files/" + encodeURIComponent(file.name) + "/versions",
            (data) => setVersions(Array.isArray(data) ? data : []),
            () => history("/login"),
            () => {
                setVersionsFile(null);
                Message.error("Failed to load the file's versions");
            });
    }, [getAccessTokenSilently, history]);

    const onDrop = useCallback(async acceptedFiles => {
        // Large images are shrunk; ask first whether to keep their originals.
        const choice = await confirmUpload(acceptedFiles);
        if (!choice) return;
        acceptedFiles.forEach((file) => {
            UploadFileApi(getAccessTokenSilently,
                "/api/files/",
                (data) => {
                    refreshRemoteFileList();
                    Message.success("File uploaded");
                },
                (response) => {
                    // setInitializing(false);
                    history("/login");
                }, (response) => {
                    // setInitializing(false);
                    Message.error("Failed to upload file");
                },
                file,
                uploadFields(choice)
            )

        })

    }, [getAccessTokenSilently, history, refreshRemoteFileList]);

    const rows = useMemo(
        () => sortFiles(filterByTag(fileAssetItems, tagFilterId), sort),
        [fileAssetItems, sort, tagFilterId]
    );
    const allSelected = rows.length > 0 && selected.length === rows.length;
    const usage = useMemo(() => tagUsage(fileAssetItems), [fileAssetItems]);
    const selectedFiles = useMemo(
        () => rows.filter((f) => selected.indexOf(f.name) > -1),
        [rows, selected]
    );

    const columns = [
        {
            title: "",
            dataIndex: "preview",
            width: 56,
            render: (_, row) => {
                const url = thumbUrl(row);
                return url ? (
                    <Image
                        src={url}
                        previewProps={{ src: previewUrl(row) }}
                        alt={row.name}
                        width={40}
                        height={40}
                        style={{ objectFit: "contain", background: "var(--color-fill-2)", borderRadius: 2 }}
                        onLoad={(e) =>
                            !row.thumb_url &&
                            e.target.naturalWidth &&
                            recordDimensions(row.name, { width: e.target.naturalWidth, height: e.target.naturalHeight })
                        }
                        error={<IconFile />}
                    />
                ) : (
                    <IconFile style={{ fontSize: 20, color: "var(--color-text-3)" }} />
                );
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            render: (name, row) => (isImageFile(row)
                ? <>{name}<br /><Button type="text" size="mini" style={{ padding: 0 }} onClick={() => showVersions(row)}>Versions</Button></>
                : name),
        },
        {
            title: "Size",
            dataIndex: "size",
            render: (size, row) => (row.full_size
                ? <>{formatBytes(size)}<br /><Text type="secondary">Full size kept: {formatBytes(row.full_size)}</Text></>
                : formatBytes(size)),
        },
        {
            title: "Dimensions",
            dataIndex: "dimensions",
            render: (_, row) => formatDimensions(fileDimensions(row, dimensions[row.name])),
        },
        {
            title: "Used in",
            dataIndex: "used_in",
            render: (_, row) => <FileUsageLinks usages={usagesFor(row.name)} />,
        },
        {
            title: "Tags",
            dataIndex: "tags",
            render: (_, row) => (
                <FileTagPicker
                    file={row}
                    allTags={fileTags}
                    onToggle={toggleTag}
                    onNewTag={openNewTagFor}
                    size="small"
                />
            ),
        },
        {
            title: "Updated",
            dataIndex: "updated_date",
            render: (d) => (d ? <span title={moment(d).format("MMM D, YYYY h:mm A")}>{moment(d).fromNow()}</span> : ""),
        },
    ];

    const rowSelection = {
        selectedRowKeys: selected,
        onChange: (selectedRowKeys) => {
            setSelected(selectedRowKeys);
        },
    };

    const emptyDescription = tagFilterId
        ? "No files with that tag"
        : rows.length > 0 && imageCount(rows) === 0
        ? "No images among your files"
        : "No files yet";

    return (
        <Content>
            <SimpleBreadcrumbs
                elements={
                    [
                        { name: 'File Assets' },
                    ]
                }

            />

            <SummaryCard title={"Upload Files"}
                         subTitle={"Make files available for sharing via email or landing pages"}
                         >
                <StyledDropZone onDrop={onDrop}/>
                <>
                    <h3>Online File Assets</h3>

                    <Row justify="space-between" align="center" style={{ marginBottom: 12, rowGap: 8 }}>
                        <Col flex="auto">
                            <Space>
                                {selected.length > 0 ? (
                                    <>
                                        <Dropdown
                                            droplist={
                                                <Menu
                                                    onClickMenuItem={(key) =>
                                                        key === "__new__"
                                                            ? openNewTagFor(selectedFiles)
                                                            : toggleTag(selectedFiles, key, true)
                                                    }
                                                    style={{ maxHeight: 280, overflow: "auto" }}
                                                >
                                                    {fileTags.map((t) => (
                                                        <Menu.Item key={t.id}>{t.name}</Menu.Item>
                                                    ))}
                                                    {fileTags.length > 0 && (
                                                        <Menu.Item
                                                            key="__divider__"
                                                            disabled
                                                            style={{ borderTop: "1px solid var(--color-border-2)" }}
                                                        />
                                                    )}
                                                    <Menu.Item key="__new__">＋ New tag…</Menu.Item>
                                                </Menu>
                                            }
                                            trigger="click"
                                            position="bl"
                                        >
                                            <Button type="outline" icon={<IconTag />}>
                                                Tag {selected.length}
                                            </Button>
                                        </Dropdown>
                                        <MyDeleteFilesDialog totalCount={selected.length} />
                                    </>
                                ) : (
                                    <Button
                                        type="primary"
                                        icon={<IconRefresh />}
                                        onClick={refreshRemoteFileList}
                                        className="dialog-button"
                                    >
                                        Refresh
                                    </Button>
                                )}
                                {view === "grid" && rows.length > 0 && (
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={selected.length > 0 && !allSelected}
                                        onChange={(checked) => setSelected(checked ? rows.map((f) => f.name) : [])}
                                    >
                                        Select all
                                    </Checkbox>
                                )}
                            </Space>
                        </Col>
                        <Col flex="none">
                            <Space>
                                <Select
                                    placeholder="All tags"
                                    allowClear
                                    style={{ width: 170 }}
                                    value={tagFilterId || undefined}
                                    onChange={(v) => {
                                        setTagFilterId(v || "");
                                        // Selections are keyed by file name, and
                                        // filtering hides rows — clear them so a
                                        // later Delete can't reach a file you can
                                        // no longer see.
                                        setSelected([]);
                                    }}
                                >
                                    {fileTags
                                        .filter((t) => usage[t.id])
                                        .map((t) => (
                                            <Select.Option key={t.id} value={t.id}>
                                                {t.name} ({usage[t.id]})
                                            </Select.Option>
                                        ))}
                                    <Select.Option key={UNTAGGED} value={UNTAGGED}>
                                        Untagged
                                    </Select.Option>
                                </Select>
                                <Text type="secondary">Sort by:</Text>
                                <Select value={sort} onChange={setSort} style={{ width: 190 }}>
                                    {SORT_OPTIONS.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <Radio.Group type="button" value={view} onChange={changeView}>
                                    <Radio value="grid" title="Grid view">
                                        <IconApps />
                                    </Radio>
                                    <Radio value="list" title="List view">
                                        <IconList />
                                    </Radio>
                                </Radio.Group>
                            </Space>
                        </Col>
                    </Row>

                    {loadError && (
                        <Alert
                            type="error"
                            title="Couldn't load file assets"
                            content="File storage isn't reachable. If you're running the app locally, the server's file storage (Amazon S3) credentials probably aren't configured — set AWS_ACCESS_KEY_ID / AWS_SECRET_ACCESS_KEY / AWS_REGION on the server and retry. If this happens in production, check the server logs."
                            action={
                                <Button size="small" type="primary" onClick={refreshRemoteFileList}>
                                    Retry
                                </Button>
                            }
                            style={{ marginBottom: 12 }}
                        />
                    )}

                    {/* One preview group across the page, so the lightbox opened from
                        any thumbnail can step through the rest with the arrows. */}
                    {!loadError && (
                        <Image.PreviewGroup infinite>
                            {view === "grid" ? (
                                rows.length === 0 ? (
                                    <Empty description={emptyDescription} />
                                ) : (
                                    <Row gutter={[16, 16]}>
                                        {rows.map((file) => (
                                            <Col key={file.name} xs={24} sm={12} md={8} lg={6} xl={6}>
                                                <FileCard
                                                    file={file}
                                                    selected={selected.indexOf(file.name) > -1}
                                                    onToggleSelect={toggleSelect}
                                                    dimensions={dimensions[file.name]}
                                                    onDimensions={recordDimensions}
                                                    allTags={fileTags}
                                                    onToggleTag={toggleTag}
                                                    onNewTag={openNewTagFor}
                                                    onDownloadFull={downloadFull}
                                                    onShowVersions={showVersions}
                                                    showUsages
                                                    usages={usagesFor(file.name)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                )
                            ) : (
                                <Table
                                    size="small"
                                    rowKey="name"
                                    columns={columns}
                                    data={rows}
                                    rowSelection={rowSelection}
                                    pagination={false}
                                    noDataElement={<Empty description={emptyDescription} />}
                                />
                            )}
                        </Image.PreviewGroup>
                    )}
                </>
            </SummaryCard>

            <Modal
                visible={pendingTagTargets !== null}
                title="New tag"
                okText="Create"
                onOk={createTag}
                okButtonProps={{ disabled: !newTagName.trim() }}
                onCancel={() => setPendingTagTargets(null)}
            >
                <Input
                    autoFocus
                    value={newTagName}
                    onChange={setNewTagName}
                    onPressEnter={createTag}
                    placeholder="e.g. Spring Sale"
                />
                {pendingTagTargets && pendingTagTargets.length > 0 && (
                    <div style={{ marginTop: 8, color: "var(--color-text-3)", fontSize: 12 }}>
                        Will be applied to {pendingTagTargets.length} file
                        {pendingTagTargets.length > 1 ? "s" : ""}.
                    </div>
                )}
            </Modal>

            <FileVersionsModal file={versionsFile} versions={versions} onClose={() => setVersionsFile(null)} />
        </Content>
    );
}
